import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/HeaderB";
import ProfileSidebar from "../components/ProfileSideBar";
import { AuthContext } from "../context/AuthContext";
import { Store, ArrowUpDown, Filter, ChevronRight, Trash2, Pencil, MapPin, Plus } from "lucide-react";

const AdminStores = () => {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [hasMore, setHasMore] = useState(true);
  const loaderRef = useRef(null);
  const initialLoad = useRef(true);
  const [expandedRow, setExpandedRow] = useState(null);
  const { user, accessToken, language } = useContext(AuthContext);
  const navigate = useNavigate();

  const [filtersVisible, setFiltersVisible] = useState(false);
  const [filterCity, setFilterCity] = useState("");
  const [filterArea, setFilterArea] = useState([]);
  const [filterStoreName, setFilterStoreName] = useState("");
  const [filterOptions, setFilterOptions] = useState({ cities: [], areas: [], storeNames: [] });

  const [sortVisible, setSortVisible] = useState(false);
  const [sortField, setSortField] = useState("store_name_el");
  const [sortOrder, setSortOrder] = useState("asc");

  const [refreshFlag, setRefreshFlag] = useState(false);
  const [deletePopup, setDeletePopup] = useState(false);
  const [storeToDelete, setStoreToDelete] = useState(null);

  const translations = {
    el: {
      title: "Διαχείριση καταστημάτων",
      filter: "Φίλτρα",
      sort: "Ταξινόμηση",
      storeName: "Κατάστημα",
      storeNameEl: "Όνομα (ΕΛ)",
      storeNameEn: "Όνομα (EN)",
      cityEl: "Πόλη (ΕΛ)",
      cityEn: "Πόλη (EN)",
      areaEl: "Περιοχή (ΕΛ)",
      areaEn: "Περιοχή (EN)",
      roadEl: "Οδός (ΕΛ)",
      roadEn: "Οδός (EN)",
      city: "Πόλη",
      area: "Περιοχή",
      road: "Οδός",
      addStore: "Προσθήκη καταστήματος",
      actions: "Ενέργειες",
      selectCity: "Επιλογή πόλης",
      selectArea: "Επιλογή περιοχής",
      selectStoreName: "Επιλογή καταστήματος",
      sortBy: "Ταξινόμηση κατά",
      asc: "Αύξουσα",
      desc: "Φθίνουσα",
      az: "Α-Ω",
      za: "Ω-Α",
      noStores: "Δεν βρέθηκαν καταστήματα.",
      delTitle: "Διαγραφή καταστήματος",
      delMsg: "Είστε σίγουροι ότι θέλετε να διαγράψετε αυτό το κατάστημα;",
      cancel: "Ακύρωση",
      delete: "Διαγραφή",
      edit: "Επεξεργασία",
      showMap: "Εμφάνιση στο χάρτη",
      loadMore: "Φόρτωση περισσότερων...",
      nmStores: "Δεν υπάρχουν άλλα καταστήματα",
      editTitle: "Επεξεργασία καταστήματος",
      save: "Αποθήκευση",
      latitude: "Γεωγρ. πλάτος",
      longitude: "Γεωγρ. μήκος",
      noCoords: "Δεν υπάρχουν συντεταγμένες",
      map: "Τοποθεσία",
    },
    en: {
      title: "Manage stores",
      filter: "Filter",
      sort: "Sort",
      storeName: "Store",
      storeNameEl: "Name (EL)",
      storeNameEn: "Name (EN)",
      cityEl: "City (EL)",
      cityEn: "City (EN)",
      areaEl: "Area (EL)",
      areaEn: "Area (EN)",
      roadEl: "Road (EL)",
      roadEn: "Road (EN)",
      city: "City",
      area: "Area",
      road: "Road",
      addStore: "Add store",
      actions: "Actions",
      selectCity: "Select city",
      selectArea: "Select area",
      selectStoreName: "Select store",
      sortBy: "Sort by",
      asc: "Ascending",
      desc: "Descending",
      az: "A-Z",
      za: "Z-A",
      noStores: "No stores found.",
      delTitle: "Delete store",
      delMsg: "Are you sure you want to delete this store?",
      cancel: "Cancel",
      delete: "Delete",
      edit: "Edit",
      showMap: "Show on map",
      loadMore: "Loading more...",
      nmStores: "No more stores",
      editTitle: "Edit store",
      save: "Save",
      latitude: "Latitude",
      longitude: "Longitude",
      noCoords: "No coordinates available",
      map: "Location",
    }
  };

  const t = translations[language] || translations.el;

  const isAlphabeticalSortField = (field) => ["store_name_el", "store_name_en", "city_el", "city_en", "area_el", "area_en", "road_el", "road_en"].includes(field);
  const getOrderLabel = (value) => {
    if (isAlphabeticalSortField(sortField)) {
      return value === "asc" ? t.az : t.za;
    }
    return value === "asc" ? t.asc : t.desc;
  };

  // ── Data fetching ─────────────────────────────────────────────────────────

  useEffect(() => {
    const fetchStores = async (pageToLoad = 1) => {
      if (!user || !accessToken) return;
      try {
        if (pageToLoad === 1 && initialLoad.current) ;//setLoading(true);
        else if (pageToLoad !== 1) setLoadingMore(true);

        const offset = (pageToLoad - 1) * pageSize;
        const params = new URLSearchParams({ sortField, sortOrder, lang: language, limit: pageSize, offset });
        if (filterCity) params.append("city", filterCity);
        if (filterArea.length > 0) params.append("areas", filterArea.join(","));
        if (filterStoreName) params.append("storeName", filterStoreName);

        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/adminstores/allstores?${params.toString()}`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        const data = await res.json();
        const fetched = data.stores || [];

        if (pageToLoad === 1) setStores(fetched);
        else setStores(prev => [...prev, ...fetched]);

        setHasMore(fetched.length === pageSize);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
        setLoadingMore(false);
        initialLoad.current = false;
      }
    };

    if (user && accessToken) {
      setStores([]);
      setPage(1);
      setHasMore(true);
      fetchStores(1);
    }
  }, [user, accessToken, sortField, sortOrder, refreshFlag, filterCity, filterArea, filterStoreName, language]);

  useEffect(() => {
    const fetchMore = async () => {
      if (!user || !accessToken || page === 1) return;
      try {
        setLoadingMore(true);
        const offset = (page - 1) * pageSize;
        const params = new URLSearchParams({ sortField, sortOrder, lang: language, limit: pageSize, offset });
        if (filterCity) params.append("city", filterCity);
        if (filterArea.length > 0) params.append("areas", filterArea.join(","));
        if (filterStoreName) params.append("storeName", filterStoreName);

        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/adminstores/allstores?${params.toString()}`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        const data = await res.json();
        const fetched = data.stores || [];
        setStores(prev => [...prev, ...fetched]);
        setHasMore(fetched.length === pageSize);
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingMore(false);
      }
    };
    fetchMore();
  }, [page]);

  // Fetch cities
  useEffect(() => {
    const fetchOptions = async () => {
      if (!accessToken) return;
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/adminstores/filter-options?lang=${language}`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        const json = await res.json();
        setFilterOptions({ cities: json.cities || [], areas: [], storeNames: json.storeNames || [] });
      } catch (err) {
        console.error(err);
      }
    };
    fetchOptions();
  }, [language, accessToken]);

  // Fetch areas when city changes
  useEffect(() => {
    const fetchAreas = async () => {
      if (!filterCity || !accessToken) {
        setFilterOptions(prev => ({ ...prev, areas: [] }));
        return;
      }
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/adminstores/filter-options?lang=${language}&city=${encodeURIComponent(filterCity)}`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        const json = await res.json();
        setFilterOptions(prev => ({ ...prev, areas: json.areas || [] }));
      } catch (err) {
        console.error(err);
      }
    };
    fetchAreas();
  }, [filterCity, language, accessToken]);

  // Infinite scroll
  useEffect(() => {
    if (!hasMore || loading || loadingMore) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) setPage(prev => prev + 1); },
      { root: null, rootMargin: '200px', threshold: 0.1 }
    );
    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => {
      if (loaderRef.current) observer.unobserve(loaderRef.current);
      observer.disconnect();
    };
  }, [hasMore, loading, loadingMore]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const toggleSelection = (value, state, setState) =>
    state.includes(value) ? setState(state.filter(v => v !== value)) : setState([...state, value]);

  const toggleExpand = (id) => setExpandedRow(expandedRow === id ? null : id);

  const handleShowOnMap = (store) => {
    if (!store.latitude || !store.longitude) { alert(t.noCoords); return; }
    navigate("/showonmap", {
      state: {
        lat: store.latitude,
        lng: store.longitude,
        storeName: language === 'el' ? store.store_name_el : store.store_name_en,
        city: language === 'el' ? store.city_el : store.city_en,
        area: language === 'el' ? store.area_el : store.area_en,
        road: language === 'el' ? store.road_el : store.road_en,
      }
    });
  };

  const handleEditOpen = (store) => {
    navigate(`/admineditstore/${store.store_id}`);
  };

  const handleDelete = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/adminstores/${storeToDelete}`,
        { method: "DELETE", headers: { Authorization: `Bearer ${accessToken}` } }
      );
      if (!res.ok) throw new Error("Failed to delete");
      setRefreshFlag(prev => !prev);
      setDeletePopup(false);
      setStoreToDelete(null);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="min-h-screen bg-custom-background flex flex-col">
      <Header />
      <div className="flex-1 flex flex-row relative bg-custom-background">
        <ProfileSidebar />
        <main id="adminstores-content" className="flex flex-col flex-1 items-center justify-start px-4 md:px-10 py-10 xl:ml-20 text-center md:text-left">

          {/* Title + controls */}
          <div className="flex justify-between items-center mb-8 gap-6 w-full max-w-6xl mt-12 md:mt-0">
            <h2 className="md:text-2xl text-lg font-semibold text-custom-letters flex items-center gap-2">
              <Store className="text-custom-grad1" /> {t.title}
            </h2>
            <div className="flex items-center gap-2">
              <button onClick={() => setFiltersVisible(!filtersVisible)} title={t.filter}
                className="bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-white p-3 rounded-full">
                <Filter size={18} />
              </button>
              <button onClick={() => setSortVisible(!sortVisible)} title={t.sort}
                className="bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-white p-3 rounded-full">
                <ArrowUpDown size={18} />
              </button>
              <button onClick={() => navigate("/addstore")} title={t.addStore}
                className="bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-white p-3 rounded-full">
                <Plus size={18} />
              </button>
            </div>
          </div>

          {/* Filter Panel */}
          {filtersVisible && (
            <div className="bg-custom-header p-6 rounded-xl shadow mb-6 w-full max-w-6xl grid grid-cols-1 md:grid-cols-3 gap-6 border border-gray-100">
              <div>
                <p className="text-sm font-semibold mb-2 text-custom-letters">{t.city}</p>
                <select value={filterCity}
                  onChange={(e) => { setFilterCity(e.target.value); setFilterArea([]); }}
                  className="w-full p-2 rounded border text-sm outline-none focus:ring-2 focus:ring-custom-grad1 bg-white">
                  <option value="">{t.selectCity}</option>
                  {filterOptions.cities.map(city => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
              </div>
              <div>
                <p className="text-sm font-semibold mb-2 text-custom-letters">{t.area}</p>
                <select disabled={!filterCity}
                  onChange={(e) => {
                    if (e.target.value) toggleSelection(e.target.value, filterArea, setFilterArea);
                    e.target.value = "";
                  }}
                  className="w-full p-2 rounded border text-sm outline-none focus:ring-2 focus:ring-custom-grad1 bg-white disabled:bg-gray-100">
                  <option value="">{t.selectArea}</option>
                  {filterOptions.areas.map(area => (
                    <option key={area} value={area}>{area}</option>
                  ))}
                </select>
              </div>
              <div>
                <p className="text-sm font-semibold mb-2 text-custom-letters">{t.storeName}</p>
                <select
                  value={filterStoreName}
                  onChange={(e) => setFilterStoreName(e.target.value)}
                  className="w-full p-2 rounded border text-sm outline-none focus:ring-2 focus:ring-custom-grad1 bg-white">
                  <option value="">{t.selectStoreName}</option>
                  {filterOptions.storeNames.map(name => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Active filter pills */}
          <div className="flex flex-wrap gap-2 mt-3 mb-6">
            {filterStoreName && (
              <span onClick={() => setFilterStoreName("")}
                className="px-3 py-1 bg-custom-grad1 text-white rounded-full cursor-pointer text-sm">
                {filterStoreName} ✕
              </span>
            )}
            {filterCity && (
              <span onClick={() => { setFilterCity(""); setFilterArea([]); }}
                className="px-3 py-1 bg-custom-grad3 text-white rounded-full cursor-pointer text-sm">
                {filterCity} ✕
              </span>
            )}
            {filterArea.map(area => (
              <span key={area} onClick={() => toggleSelection(area, filterArea, setFilterArea)}
                className="px-3 py-1 bg-custom-grad2 text-white rounded-full cursor-pointer text-sm">
                {area} ✕
              </span>
            ))}
          </div>

          {/* Sort Panel */}
          {sortVisible && (
            <div className="bg-custom-header p-4 rounded-xl shadow mb-6 w-full max-w-xs">
              <p className="text-sm font-semibold mb-2 text-custom-letters">{t.sortBy}</p>
              <select value={sortField} onChange={(e) => setSortField(e.target.value)}
                className="w-full p-2 rounded border mb-3 bg-white">
                <option value="store_name_el">{t.storeNameEl}</option>
                <option value="store_name_en">{t.storeNameEn}</option>
                <option value="city_el">{t.cityEl}</option>
                <option value="city_en">{t.cityEn}</option>
                <option value="area_el">{t.areaEl}</option>
                <option value="area_en">{t.areaEn}</option>
                <option value="road_el">{t.roadEl}</option>
                <option value="road_en">{t.roadEn}</option>
              </select>
              <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}
                className="w-full p-2 rounded border bg-white">
                <option value="asc">{getOrderLabel("asc")}</option>
                <option value="desc">{getOrderLabel("desc")}</option>
              </select>
            </div>
          )}

          <div className="w-full max-w-6xl bg-white/40 rounded-2xl shadow-lg p-4 sm:p-6 backdrop-blur-sm">

            {/* Desktop Table */}
            <div className="hidden xl:block overflow-x-auto">
              <table className="min-w-full text-sm text-custom-letters text-center border-collapse">
                <thead className="border-b font-medium">
                  <tr>
                    <th className="px-4 py-3 whitespace-nowrap">{t.storeNameEl}</th>
                    <th className="px-4 py-3 whitespace-nowrap">{t.storeNameEn}</th>
                    <th className="px-4 py-3 whitespace-nowrap">{t.cityEl}/(En)</th>
                    <th className="px-4 py-3 whitespace-nowrap">{t.areaEl}/(En)</th>
                    <th className="px-4 py-3 whitespace-nowrap">{t.roadEl}/(En)</th>
                    <th className="px-4 py-3 whitespace-nowrap">{t.map}</th>
                    <th className="px-4 py-3 whitespace-nowrap">{t.actions}</th>
                  </tr>
                </thead>
                <tbody>
                  {stores.length > 0 ? stores.map(store => (
                    <tr key={store.store_id} className="border-b">
                      <td className="px-4 py-3 text-custom-letters whitespace-nowrap">{store.store_name_el}</td>
                      <td className="px-4 py-3 text-custom-letters whitespace-nowrap">{store.store_name_en}</td>
                      <td className="px-4 py-3">{store.city_el} / {store.city_en}</td>
                      <td className="px-4 py-3">{store.area_el} / {store.area_en}</td>
                      <td className="px-4 py-3">{store.road_el} / {store.road_en}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {store.latitude && store.longitude ? (
                          <button onClick={() => handleShowOnMap(store)} title={t.showMap}
                            className="text-custom-grad4 hover:underline flex items-center justify-center gap-1 mx-auto">
                            <MapPin size={16} />
                            <span>{language === 'el' ? store.store_name_el : store.store_name_en}</span>
                          </button>
                        ) : (
                          <span className="text-gray-400 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-center gap-2">
                          <button onClick={() => handleEditOpen(store)} title={t.edit}
                            className="bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-white p-3 rounded-full">
                            <Pencil size={18} />
                          </button>
                          <button onClick={() => { setStoreToDelete(store.store_id); setDeletePopup(true); }} title={t.delete}
                            className="bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-white p-3 rounded-full">
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan="7" className="py-6 italic text-custom-letters">{t.noStores}</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile view */}
            <div className="block xl:hidden">
              {stores.length === 0 && <p className="py-6 italic text-custom-letters text-center">{t.noStores}</p>}
              {stores.map(store => {
                const isExpanded = expandedRow === store.store_id;
                return (
                  <div key={store.store_id}
                    className="border border-gray-300 rounded-xl mb-4 bg-custom-background/60 shadow-sm overflow-hidden">
                    <div className="flex items-center px-3 py-3 gap-2">
                      <button onClick={() => toggleExpand(store.store_id)}
                        className="p-1 hover:bg-gray-200 rounded-full transition-transform duration-200"
                        style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                        <ChevronRight size={20} className="text-gray-500" />
                      </button>
                      <div className="flex-1 min-w-0">
                        <span className="block font-semibold text-custom-letters truncate text-sm">
                          {language === 'el' ? store.store_name_el : store.store_name_en}
                        </span>
                        <span className="text-xs text-custom-letters">
                          {language === 'el' ? store.city_el : store.city_en}
                        </span>
                      </div>
                      <div className="flex gap-1.5 flex-shrink-0">
                        <button onClick={() => handleEditOpen(store)} title={t.edit}
                          className="bg-gradient-to-r from-custom-grad1 to-custom-grad4 text-white p-2 rounded-full shadow-sm">
                          <Pencil size={13} />
                        </button>
                        <button onClick={() => { setStoreToDelete(store.store_id); setDeletePopup(true); }} title={t.delete}
                          className="bg-gradient-to-r from-custom-grad1 to-custom-grad4 text-white p-2 rounded-full shadow-sm">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="px-10 pb-4 text-xs text-custom-letters text-left border-t border-gray-100 pt-3 bg-white/30">
                        <p className="mb-1"><span className="font-semibold">{t.storeNameEl}:</span> {store.store_name_el}</p>
                        <p className="mb-1"><span className="font-semibold">{t.storeNameEn}:</span> {store.store_name_en}</p>
                        <p className="mb-1"><span className="font-semibold">{t.cityEl}/(EN):</span> {store.city_el} / {store.city_en}</p>
                        <p className="mb-1"><span className="font-semibold">{t.areaEl}/(EN):</span> {store.area_el} / {store.area_en}</p>
                        <p className="mb-1"><span className="font-semibold">{t.roadEl}/(EN):</span> {store.road_el} / {store.road_en}</p>
                        {store.latitude && store.longitude && (
                          <p className="mt-2 flex flex-row items-center gap-1">
                            <span className="font-semibold whitespace-nowrap">{t.map}:</span>
                            <button
                              onClick={() => handleShowOnMap(store)}
                              className="text-custom-grad4 underline cursor-pointer flex items-center gap-1 whitespace-nowrap"
                            >
                              <MapPin size={13} />
                              <span className="whitespace-nowrap">{language === 'el' ? store.store_name_el : store.store_name_en}</span>
                            </button>
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div ref={loaderRef} className="h-10 w-full max-w-6xl" />
          {loadingMore && <div className="text-center mt-2">{t.loadMore}</div>}
          {!hasMore && !loading && stores.length > 0 && (
            <div className="text-center mt-2 text-custom-letters">{t.nmStores}</div>
          )}

          {/* Delete Confirmation Popup */}
          {deletePopup && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div role="dialog" aria-modal="true" aria-labelledby="adminstores-delete-title" className="bg-custom-header rounded-2xl p-6 w-80 shadow-xl text-center">
                <h3 id="adminstores-delete-title" className="text-lg font-semibold text-custom-letters mb-3">{t.delTitle}</h3>
                <p className="text-sm text-custom-letters mb-6">{t.delMsg}</p>
                <div className="flex justify-center gap-4">
                  <button onClick={() => setDeletePopup(false)}
                    className="px-6 py-2 rounded-full bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-white hover:opacity-90 transition">
                    {t.cancel}
                  </button>
                  <button onClick={handleDelete}
                    className="px-6 py-2 rounded-full bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-white hover:opacity-90 transition">
                    {t.delete}
                  </button>
                </div>
              </div>
            </div>
          )}

        </main>
      </div>
    </div>
  );
};

export default AdminStores;